import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import AdsList from '../../AdsList'
import LineItemsList from '../LineItemsList'
import SummaryGraphs from '../../../../features/components/SummaryGraphs'
import PageWelcomeHeading from '../PageWelcomeHeading'
import SuccessModal from '../../../../features/components/Modals/SuccessModal'
import AdCopyPreviewsButton from '../../../../features/components/Buttons/AdCopyPreviewsButton'

import { redirectTo } from '../../../../helpers/url'

import { openForm } from '../../../../modules/actions/forms'
import { campaignWasLoadedSelector } from '../../../../modules/selectors/campaigns'
import { campaignGraphsIsLoadingSelector } from '../../../../modules/selectors/campaignsSummary'
import {
  lineItemsErrorSelector,
  lineItemsSelector,
  lineItemsWasLoadedSelector
} from '../../../../modules/selectors/lineItems'
import { adsIsLoadingSelector, adsSelector } from '../../../../modules/selectors/ads'

import { ROUTE_PARAMS, ROUTES } from '../../../../constants/routes'
import { AD_FACEBOOK_CREATE, AD_TIKTOK_CREATE } from '../../../../constants/forms'
import { MODAL_TYPE } from '../../../../constants/other'
import { FACEBOOK_PLATFORM, TIKTOK_PLATFORM } from '../../../../constants/selectLists/platformList'

function ReusableCampaignsSummaryContent({
  selectedPlatform,
  campaignName,
  DateRangeLabel,
  adsSectionRef,
  scrollToAdsSectionHandler,
  scrollToReportsSectionHandler,
  reusableSummaryGraphsProps
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { campaign_id: campaignId } = useParams()

  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const lineItems = useSelector(lineItemsSelector)
  const lineItemsError = useSelector(lineItemsErrorSelector)
  const lineItemsWasLoaded = useSelector(lineItemsWasLoadedSelector)
  const adsList = useSelector(adsSelector)
  const adsIsLoading = useSelector(adsIsLoadingSelector)
  const graphsIsLoading = useSelector(campaignGraphsIsLoadingSelector)

  const lineItemsEmpty = !lineItems.length
  const adsEmpty = !adsList.length

  const lineItemsLoaded = lineItemsWasLoaded || lineItemsError

  // check for both wasLoaded and error states
  const isPageDataLoading = !campaignWasLoaded || !lineItemsLoaded || adsIsLoading || graphsIsLoading

  const openAdCreateFormInFirstLineItem = useCallback(() => {
    // Redirect to first created lineItem and open create ad form
    // here we use index [1] because we always have total item, which is index [0]
    const firstLineItem = lineItems[1]

    let formName
    if (selectedPlatform === FACEBOOK_PLATFORM) {
      formName = AD_FACEBOOK_CREATE
    } else if (selectedPlatform === TIKTOK_PLATFORM) {
      formName = AD_TIKTOK_CREATE
    }

    redirectTo(`${ROUTES.lineItemsSummary}`, {
      [ROUTE_PARAMS.campaignId]: campaignId,
      line_item_id: firstLineItem.id
    })
    dispatch(openForm({ formName }))
  }, [dispatch, selectedPlatform, campaignId, lineItems])

  return (
    <>
      <PageWelcomeHeading
        isPageDataLoading={isPageDataLoading}
        campaignName={campaignName}
        scrollToAdsSectionHandler={scrollToAdsSectionHandler}
        scrollToReportsSectionHandler={scrollToReportsSectionHandler}
      />

      <LineItemsList title={selectedPlatform === TIKTOK_PLATFORM ? 'List of Ad Groups' : 'List of Ad Sets'} />

      {lineItemsLoaded && !lineItemsEmpty && (
        // render ads and report after we know that lineItems was fetched
        // to prevent flicking these sections with "no data" text
        <>
          <AdsList
            scrollRef={adsSectionRef}
            RightPanel={
              <>
                {DateRangeLabel}
                {/*on the campaign summary page no need to show a 'Copy Previews' button for TikTok platform*/}
                {selectedPlatform !== TIKTOK_PLATFORM && <AdCopyPreviewsButton />}
              </>
            }
          />
          <SummaryGraphs hideGraphsSection={adsEmpty} {...reusableSummaryGraphsProps} />
        </>
      )}
      <SuccessModal
        successButtonAction={openAdCreateFormInFirstLineItem}
        successButtonText={t('Create ad in first ad set')}
        modalType={MODAL_TYPE.automatedLineItemCreated}
      >
        <p>{t('Your ad sets were created successfully.')}</p>
        <p>
          {t(
            'The next step is to create ads in all the ad sets. We recommend creating around 3 significantly different ads (if possible) per ad set for best results.'
          )}
        </p>
      </SuccessModal>
    </>
  )
}

ReusableCampaignsSummaryContent.propTypes = {
  selectedPlatform: PropTypes.string.isRequired,
  campaignName: PropTypes.string,
  DateRangeLabel: PropTypes.node,
  adsSectionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  scrollToAdsSectionHandler: PropTypes.func.isRequired,
  scrollToReportsSectionHandler: PropTypes.func.isRequired,
  reusableSummaryGraphsProps: PropTypes.object.isRequired
}

export default ReusableCampaignsSummaryContent
